import { useMemo, useState } from "react";
import { CalendarClock, CalendarDays, Play } from "lucide-react";
import { format, parseISO } from "date-fns";
import { enAU } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { http, errMsg, DOMAIN_SHORT } from "@/lib/api";
import { useAuth } from "@/context/Auth";

const DAY = 86400000;
const PER_DAY = 2;
const UNTRIED_SCORE = 45;

const today = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const daysUntil = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return Math.round((new Date(y, m - 1, d) - today()) / DAY);
};

export const buildPlan = (stats, days) => {
  const ranked = [...stats].sort((a, b) =>
    (a.avg_score ?? UNTRIED_SCORE) - (b.avg_score ?? UNTRIED_SCORE) || a.attempts - b.attempts);
  const order = [...ranked, ...ranked.filter((d) => (d.avg_score ?? UNTRIED_SCORE) < 60)];
  return Array.from({ length: Math.min(days, 7) }, (_, i) => {
    const picks = [];
    for (let j = 0; picks.length < PER_DAY && j < order.length; j++) {
      const d = order[(i * PER_DAY + j) % order.length];
      if (!picks.some((p) => p.number === d.number)) picks.push(d);
    }
    return { date: new Date(today().getTime() + i * DAY), domains: picks };
  });
};

const useExamDate = () => {
  const { user, setUser } = useAuth();
  const [local, setLocal] = useState(() => localStorage.getItem("exam-date") || "");
  const value = user ? user.exam_date || "" : user === false ? local : "";
  const save = async (v) => {
    if (!user) {
      v ? localStorage.setItem("exam-date", v) : localStorage.removeItem("exam-date");
      return setLocal(v);
    }
    try {
      const { data } = await http.put("/me/exam-date", { exam_date: v || null });
      setUser({ ...user, exam_date: data.exam_date });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return [value, save];
};

const PlanDay = ({ day, i, onOpen }) => (
  <div data-testid={`exam-plan-day-${i}`} className="flex flex-wrap items-center gap-3 py-2.5 border-t first:border-t-0" style={{ borderColor: "var(--line)" }}>
    <span className="font-mono text-xs w-24 shrink-0" style={{ color: i === 0 ? "var(--accent)" : "var(--ink-3)" }}>
      {i === 0 ? "TODAY" : day.date.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}
    </span>
    {day.domains.map((d) => (
      <button key={d.number} data-testid={`exam-plan-domain-${i}-${d.number}`} onClick={() => onOpen(d.number)}
        className="btn-ghost h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5">
        <Play size={11} style={{ color: "var(--accent)" }} /> D{d.number} {DOMAIN_SHORT[d.number - 1]}
        <span className="font-mono" style={{ color: "var(--ink-3)" }}>{d.avg_score == null ? "new" : `${d.avg_score}%`}</span>
      </button>
    ))}
  </div>
);

export const ExamPlan = ({ stats, onOpen }) => {
  const [date, save] = useExamDate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const days = date ? daysUntil(date) : null;
  const plan = useMemo(() => (days > 0 && stats.length ? buildPlan(stats, days) : []), [stats, days]);

  return (
    <section data-testid="exam-countdown-plan" className="mt-14">
      <h2 className="font-display text-lg font-bold flex items-center gap-2"><CalendarClock size={18} /> Exam countdown plan</h2>
      <div className="mt-4 card p-5">
        <div className="flex flex-wrap items-center gap-4">
          <label className="text-sm flex items-center gap-2">
            My SSSA date
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <button data-testid="exam-date-input" type="button" className="h-9 rounded-lg px-3 text-sm flex items-center gap-2"
                  style={{ background: "var(--muted-bg)", border: "1px solid var(--line)", color: "var(--ink)" }}>
                  <CalendarDays size={14} /> {date ? format(parseISO(date), "d MMM yyyy", { locale: enAU }) : "dd/mm/yyyy"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start" style={{ background: "var(--surface)" }}>
                <Calendar mode="single" locale={enAU} weekStartsOn={1} selected={date ? parseISO(date) : undefined}
                  disabled={{ before: today() }} onSelect={(d) => { if (d) { save(localISO(d)); setPickerOpen(false); } }} initialFocus />
              </PopoverContent>
            </Popover>
          </label>
          {date && <button data-testid="exam-date-clear-button" onClick={() => save("")} className="text-xs underline" style={{ color: "var(--ink-3)" }}>Clear</button>}
          {days != null && (
            <span data-testid="exam-days-remaining" className="ml-auto font-mono text-sm">
              {days > 0 ? <><span className="text-3xl font-bold" style={{ color: days <= 14 ? "var(--alert)" : "var(--accent)" }}>{days}</span> day{days === 1 ? "" : "s"} to go</> : days === 0 ? "Exam day — good luck!" : "Exam date has passed"}
            </span>
          )}
        </div>
        {!date && <p className="mt-3 text-sm" style={{ color: "var(--ink-3)" }}>Set your exam date to get a daily practice plan weighted towards your weakest domains.</p>}
        {plan.length > 0 && (
          <div className="mt-4" data-testid="exam-plan-list">
            <p className="text-xs mb-1" style={{ color: "var(--ink-3)" }}>Next {plan.length} day{plan.length === 1 ? "" : "s"}: {PER_DAY} cases a day, weakest and untried domains come up most often. It updates as you practise.</p>
            {plan.map((day, i) => <PlanDay key={i} day={day} i={i} onOpen={onOpen} />)}
          </div>
        )}
      </div>
    </section>
  );
};
